import { useInfiniteQuery, keepPreviousData } from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";
import { PaginatedResponse } from "@/shared/http";

import {
  type TriggerEvaluationRecord,
  TriggerType,
} from "../../domain/actions";

interface UseTriggerEvaluationsQueryOptions {
  filter: {
    datasetId?: string;
    triggerName?: string;
    triggerType?: TriggerType;
  };
  limit?: number;
}

/**
 * Tanstack query key for querying trigger evaluations
 */
export function getQueryKey(options: UseTriggerEvaluationsQueryOptions) {
  const { filter, limit = 50 } = options;
  return [
    "triggerEvaluations",
    filter.datasetId,
    filter.triggerName,
    filter.triggerType,
    limit,
  ];
}

/**
 * Hook to query trigger evaluations with pagination support.
 *
 * Fetches trigger evaluations for either a dataset or a trigger with
 * configurable pagination. Uses React Query's infinite query pattern
 * for efficient pagination.
 *
 * @param options - Configuration options
 * @param options.filter.datasetId - Dataset ID to fetch evaluations for (mutually exclusive with triggerName)
 * @param options.filter.triggerName - Trigger name to fetch evaluations for (mutually exclusive with datasetId)
 * @param options.filter.triggerType - Trigger type (required when using triggerName)
 * @param options.limit - Number of evaluations per page (default: 50)
 *
 * @example
 * ```tsx
 * const evaluationsQuery = useTriggerEvaluationsQuery({
 *   filter: { datasetId: "dataset_123" },
 *   limit: 50,
 * });
 * ```
 */
export function useTriggerEvaluationsQuery(
  options: UseTriggerEvaluationsQueryOptions,
) {
  const { triggerService } = useDomainServices();
  const { filter, limit = 50 } = options;
  const { datasetId, triggerName, triggerType } = filter;

  return useInfiniteQuery({
    queryKey: getQueryKey(options),
    queryFn: async ({ pageParam, signal }) => {
      if (datasetId !== undefined && triggerName !== undefined) {
        throw new Error("Cannot specify both datasetId and triggerName");
      }

      if (datasetId !== undefined) {
        return triggerService.getPagedTriggerEvaluationsForDataset(datasetId, {
          abortSignal: signal,
          limit,
          pageToken: pageParam,
        });
      }

      if (triggerName !== undefined) {
        return triggerService.getPagedTriggerEvaluationsForTrigger(
          triggerName,
          {
            abortSignal: signal,
            limit,
            pageToken: pageParam,
            triggerType,
          },
        );
      }

      return Promise.resolve<PaginatedResponse<TriggerEvaluationRecord>>({
        items: [],
        next_token: null,
        total_count: null,
      });
    },
    select: (data) => {
      return data.pages.flatMap((page) => page.items);
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage: PaginatedResponse<TriggerEvaluationRecord>) =>
      lastPage.next_token ?? undefined,
    enabled: datasetId !== undefined || triggerName !== undefined,
    placeholderData: keepPreviousData,
  });
}
